import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from './Shared';
type Slide = {
  src: string;
  alt: string;
  title: string;
  caption: string;
};
const slides: Slide[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/uunVEPRwoyjBGsZTfwjieC/Screenshot_20260528-112429~2.png',
  alt: 'Ambay — Acción farmacológica e indicaciones',
  title: 'TOMO I · ENCICLOPEDIA A-Z',
  caption: 'Fichas técnicas completas con semáforo de seguridad'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kfTJBTFpsHwtimJA1GSPsZ/Screenshot_20260528-113009~2.png',
  alt: 'Protocolos por Sistema: Sistema Digestivo',
  title: 'TOMO II · PROTOCOLOS CLÍNICOS',
  caption: 'Mapas terapéuticos por sistema con plantas nativas de acción específica'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/sVQ7VbiChq5kj6BL9NhPyM/Screenshot_20260528-112733~2.png',
  alt: 'Paso 2: La calcinación — Separación de la sal',
  title: 'TOMO III · PREPARACIONES PROFESIONALES',
  caption: 'Procesos paso a paso para elaborar tinturas, sales y extractos'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/i1Wbywum6wy1HwRuBVGvJh/Screenshot_20260528-112610~2.png',
  alt: 'Fórmula 1 — Rompe Piedras',
  title: 'BONUS · RECETARIO MAESTRO',
  caption: 'Fórmulas listas para usar en consulta, en versión infusión y extracto'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/8679751a-e1cc-467c-9d07-3fa100047435.jpg',
  alt: 'Tomo IV — Alquimia Nativa',
  title: 'TOMO IV · ALQUIMIA NATIVA',
  caption: 'Espagiria vegetal y fitococina terapéutica argentina'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/xg1nrR5aina9jytz5fxJsq/Screenshot_20260528-112522~2.png',
  alt: 'Hábitos y Estilo de Vida — Ficha de paciente',
  title: 'BONUS · FICHAS DE CONSULTORIO',
  caption: 'Planillas clínicas profesionales para evaluación integral del paciente'
}];
export const MaterialPreview = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + slides.length) % slides.length);
  }, [index]);
  const next = useCallback(() => {
    setDirection(1);
    setIndex(i => (i + 1) % slides.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setIndex(i => (i - 1 + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next, paused]);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null || slides.length <= 1) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();else prev();
    }
    setTouchStartX(null);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0,
      scale: 0.96
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0,
      scale: 0.96
    })
  };
  return <Section className="max-w-5xl" data-id="element-216" data-mp="1gy6y72u5fi92">
      <div className="text-center mb-8 md:mb-12" data-id="element-217" data-mp="1cxwq9kmsqhrk">
        <div className="inline-flex items-center justify-center bg-white/80 border border-brand-purple/40 px-4 py-1.5 rounded-full mb-5 shadow-sm" data-id="element-218" data-mp="1cygchdjhbsbe">
          <span className="font-serif font-semibold tracking-wider text-brand-purpleDark text-xs md:text-sm" data-id="element-219" data-mp="37bo0al91f3nj">
            VISTA PREVIA DEL KIT
          </span>
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold leading-tight text-brand-purpleDark" data-id="element-220" data-mp="32arw06krxfhl">
          📸 Conocé el material por dentro
        </h2>
        <p className="text-brand-text/70 mt-4 text-sm md:text-base max-w-2xl mx-auto" data-id="element-221" data-mp="23zqms143kuyr">
          4 tomos, bonus y planillas clínicas — todo diseñado para usar en el
          consultorio desde la primera consulta.
        </p>
      </div>

      <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-222" data-mp="1v26jkmmocoli">
        <div className="relative rounded-3xl overflow-hidden border border-brand-purple/30 bg-gradient-to-br from-brand-purpleDark/95 via-brand-purple/90 to-brand-greenMoss/90 shadow-[0_20px_60px_rgba(45,95,63,0.25)]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-223" data-mp="1l2fuj0gvv1st">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none" data-id="element-224" data-mp="xdwic9upq4vg"></div>

          <div className="relative w-full flex items-center justify-center px-4 md:px-16 py-8 md:py-12" style={{
          minHeight: '420px'
        }} data-id="element-225" data-mp="ipcxwtp6vzkz">
            <AnimatePresence custom={direction} mode="wait" data-id="element-226" data-mp="solpxh0hv44p">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 260,
                damping: 30
              },
              opacity: {
                duration: 0.3
              },
              scale: {
                duration: 0.3
              }
            }} className="flex flex-col items-center justify-center w-full" data-id="element-227" data-mp="slzjjkpcauo2">
                <div className="relative" data-id="element-228" data-mp="1gf7z5bm8iuga">
                  <div className="absolute -inset-4 bg-white/20 blur-2xl rounded-3xl" data-id="element-229" data-mp="1nguwlwgsmbgj"></div>
                  <img src={slides[index].src} alt={slides[index].alt} draggable={false} className="relative max-h-[420px] md:max-h-[520px] w-auto max-w-full rounded-xl shadow-2xl ring-1 ring-white/20 select-none" data-id="element-230" data-mp="1sfxu8vug1feh" />
                </div>
                <div className="relative mt-5 md:mt-6 text-center max-w-md" data-id="element-231" data-mp="2684cqol2jyh3">
                  <p className="font-display font-bold text-white text-sm md:text-lg tracking-wide drop-shadow" data-id="element-232" data-mp="2b65t31r41jov">
                    {slides[index].title}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {slides.length > 1 && <>
              <button type="button" onClick={prev} aria-label="Anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-purple/40 shadow-md flex items-center justify-center text-brand-purple hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-233" data-mp="lsj484gq2gpg">
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-234" data-mp="2v91w5n0hxpww" />
              </button>
              <button type="button" onClick={next} aria-label="Siguiente" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-purple/40 shadow-md flex items-center justify-center text-brand-purple hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-235" data-mp="2qaqn3ei0zjfq">
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-236" data-mp="3921c1q46xm4z" />
              </button>
            </>}
        </div>

        <div className="mt-5 text-center px-4 min-h-[2.5rem]" data-id="element-237" data-mp="1xl1mv3d53nf6">
          <AnimatePresence mode="wait" data-id="element-238" data-mp="1xjgunkeasgkh">
            <motion.p key={index} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.25
          }} className="text-sm md:text-base font-medium text-brand-purpleDark" data-id="element-239" data-mp="1h3y1dcyntzo">
              {slides[index].caption}
            </motion.p>
          </AnimatePresence>
        </div>

        {slides.length > 1 && <>
            <div className="flex items-center justify-center gap-2 mt-4" data-id="element-240" data-mp="ahsai3mphmag">
              {slides.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a la imagen ${i + 1}`} className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-brand-purple' : 'w-2 bg-brand-purple/20 hover:bg-brand-purple/50'}`} data-id="element-241" data-mp="ffbqp92ys951" data-mpi={i} />)}
            </div>
            <p className="text-center text-xs text-brand-muted mt-3" data-id="element-242" data-mp="2nornda6xgfdv">
              {index + 1} / {slides.length} · Deslizá o usá las flechas
            </p>
          </>}
      </div>
    </Section>;
};